import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Collapse } from '@mui/material';
import {
  Bot,
  Brain,
  FileText,
  Volume2,
  ArrowLeftRight,
} from 'lucide-react';

import { Button, Chip, CoordioDropdown, FormField, Input, Switch, Textarea, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

import { PageFormLayout } from '../layouts/PageFormLayout';
import { agentService } from '../../services/agentService';
import { llmService } from '../../services/llmService';
import { useNotificationStore } from '../../store/notificationStore';
import { CodeEditor } from '../common/CodeEditor';
import { IoModelEditor } from '../common/IoModelEditor';
import {
  type IoField,
  parametersToTree,
  treeToParameters,
  schemaToTree,
  treeToSchema,
  validateTree,
  collectLeafPaths,
  extractTemplateVariables,
} from '../../utils/ioModel';

import type {
  AgentDefinitionDto,
  AgentSpeechConfig,
  CreateAgentRequest,
  UpdateAgentRequest,
  CreateAgentParameterRequest,
  UpdateAgentParameterRequest,
} from '../../types/agent';
import type { LlmModelDto } from '../../types/llm';

const CATEGORIES = [
  'Infrastructure Management',
  'Security & Compliance',
  'Financial Operations',
  'User Support',
  'Monitoring & Observability',
  'Automation & Orchestration',
];

const categoryOptions = [
  { label: 'None', value: '' },
  ...CATEGORIES.map((cat) => ({ label: cat, value: cat })),
];

const SPEECH_LANGUAGES = [
  'en-US',
  'en-GB',
  'en-IN',
  'en-AU',
  'de-DE',
  'fr-FR',
  'es-ES',
  'it-IT',
  'pt-BR',
  'hi-IN',
  'ja-JP',
  'zh-CN',
];

const speechLanguageOptions = [
  { label: 'Default (en-US)', value: '' },
  ...SPEECH_LANGUAGES.map((lang) => ({ label: lang, value: lang })),
];

export const AgentForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [_agent, setAgent] = useState<AgentDefinitionDto | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [outputSchemaJson, setOutputSchemaJson] = useState('{}');
  const [userPromptTemplate, _setUserPromptTemplate] = useState('');
  const [category, setCategory] = useState('');
  const [iconName, setIconName] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [includeAllDefaultTools, setIncludeAllDefaultTools] = useState(false);
  const [fanOut, setFanOut] = useState(false);
  const [useOpenAiHostedSkills, setUseOpenAiHostedSkills] = useState(false);
  const [useHarnessAgent, setUseHarnessAgent] = useState(false);
  const [injectSkillInstructionsAsUserContext, setInjectSkillInstructionsAsUserContext] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [defaultModelId, setDefaultModelId] = useState<string>('');
  const [contextLimitLastNMessages, setContextLimitLastNMessages] = useState<string>('');
  const [contextLimitLastNDays, setContextLimitLastNDays] = useState<string>('');
  const [availableModels, setAvailableModels] = useState<LlmModelDto[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);

  const [speechLanguage, setSpeechLanguage] = useState('');
  const [speechDiarization, setSpeechDiarization] = useState(true);
  const [speechDiarizeIntermediate, setSpeechDiarizeIntermediate] = useState(true);
  const [speechMaxSpeakers, setSpeechMaxSpeakers] = useState('');
  const [speechPhrases, setSpeechPhrases] = useState<string[]>([]);
  const [speechPhraseInput, setSpeechPhraseInput] = useState('');
  const [speechWeight, setSpeechWeight] = useState('');

  const [inputFields, setInputFields] = useState<IoField[]>([]);
  const [outputFields, setOutputFields] = useState<IoField[]>([]);
  const [outputRootExtra, setOutputRootExtra] = useState<Record<string, unknown>>({});
  const [outputSupported, setOutputSupported] = useState(true);
  const [showOutputJson, setShowOutputJson] = useState(false);
  const [ioEditorKey, setIoEditorKey] = useState(0);

  const { addNotification } = useNotificationStore();

  const selectedModelSupportsHostedSkills = useMemo(() => {
    if (!defaultModelId) return false;
    return (
      availableModels.find((m) => m.id === defaultModelId)?.providerSupportsHostedSkills ?? false
    );
  }, [defaultModelId, availableModels]);

  useEffect(() => {
    if (!defaultModelId || !useOpenAiHostedSkills) return;
    const m = availableModels.find((x) => x.id === defaultModelId);
    if (m && !m.providerSupportsHostedSkills) {
      setUseOpenAiHostedSkills(false);
    }
  }, [defaultModelId, availableModels, useOpenAiHostedSkills]);

  useEffect(() => {
    if (isEdit && id) {
      loadAgent();
    }
    loadAvailableModels();
  }, [id, isEdit]);

  const loadAvailableModels = async () => {
    setModelsLoading(true);
    try {
      const models = await llmService.getAvailableModels();
      setAvailableModels(models);
    } catch {
      try {
        const allModels = await llmService.getModels(undefined, true);
        setAvailableModels(allModels);
      } catch {
        // ignore
      }
    } finally {
      setModelsLoading(false);
    }
  };

  const modelOptions = useMemo(() => {
    const opts: { label: string; value: string }[] = [{ label: 'None (use system default)', value: '' }];
    const grouped = availableModels.reduce<Record<string, LlmModelDto[]>>((acc, model) => {
      const key = model.providerName;
      if (!acc[key]) acc[key] = [];
      acc[key].push(model);
      return acc;
    }, {});
    for (const [providerName, models] of Object.entries(grouped)) {
      for (const model of models) {
        const ctx = model.maxContextTokens >= 1000
          ? `${Math.round(model.maxContextTokens / 1000)}K`
          : String(model.maxContextTokens);
        opts.push({
          label: `[${providerName}] ${model.displayName} (${ctx} ctx)`,
          value: model.id,
        });
      }
    }
    return opts;
  }, [availableModels]);

  const loadAgent = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const data = await agentService.getAgent(id);
      setAgent(data);
      setName(data.name);
      setSlug(data.slug);
      setDescription(data.description || '');
      setSystemPrompt(data.systemPrompt);
      setOutputSchemaJson(data.outputSchemaJson || '{}');
      setCategory(data.category || '');
      setIconName(data.iconName || '');
      setSortOrder(data.sortOrder);
      setIncludeAllDefaultTools(data.includeAllDefaultTools);
      setFanOut(data.fanOut ?? false);
      const hostedOk =
        !data.defaultModelId ||
        (data.defaultModelProviderSupportsHostedSkills ?? false);
      setUseOpenAiHostedSkills(hostedOk && (data.useOpenAiHostedSkills ?? false));
      setUseHarnessAgent(data.useHarnessAgent ?? false);
      setInjectSkillInstructionsAsUserContext(data.injectSkillInstructionsAsUserContext ?? false);
      setIsActive(data.isActive);
      setDefaultModelId(data.defaultModelId || '');
      setContextLimitLastNMessages(data.contextLimitLastNMessages != null ? String(data.contextLimitLastNMessages) : '');
      setContextLimitLastNDays(data.contextLimitLastNDays != null ? String(data.contextLimitLastNDays) : '');
      if (data.speechConfigJson) {
        try {
          const speech = JSON.parse(data.speechConfigJson) as AgentSpeechConfig;
          setSpeechLanguage(speech.language || '');
          setSpeechDiarization(speech.diarization ?? true);
          setSpeechDiarizeIntermediate(speech.diarizeIntermediateResults ?? true);
          setSpeechMaxSpeakers(speech.maxSpeakers != null ? String(speech.maxSpeakers) : '');
          setSpeechPhrases(speech.phraseList?.phrases ?? []);
          setSpeechWeight(speech.phraseList?.weight != null ? String(speech.phraseList.weight) : '');
        } catch {
          // Malformed stored config: start from defaults rather than blocking the form.
        }
      }
      setInputFields(parametersToTree(data.parameters));
      const parsedSchema = schemaToTree(data.outputSchemaJson || '{}');
      setOutputFields(parsedSchema.fields);
      setOutputRootExtra(parsedSchema.rootExtra);
      setOutputSupported(parsedSchema.supported);
      setIoEditorKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load agent');
      addNotification(err.response?.data?.message || 'Failed to load agent', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isEdit && name) {
      const generatedSlug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setSlug(generatedSlug);
    }
  }, [name, isEdit]);

  const handleSubmit = async () => {
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!systemPrompt.trim()) {
      setError('System prompt is required');
      return;
    }

    if (outputSchemaJson.trim()) {
      try {
        JSON.parse(outputSchemaJson);
      } catch {
        setError('Output schema must be valid JSON.');
        return;
      }
    }

    const treeProblems = [...validateTree(inputFields), ...(outputSupported ? validateTree(outputFields) : [])];
    if (treeProblems.length > 0) {
      setError(treeProblems[0]);
      return;
    }

    const nMessages = contextLimitLastNMessages.trim() ? parseInt(contextLimitLastNMessages.trim(), 10) : null;
    const nDays = contextLimitLastNDays.trim() ? parseInt(contextLimitLastNDays.trim(), 10) : null;
    if (nMessages != null && (Number.isNaN(nMessages) || nMessages < 1 || nMessages > 500)) {
      setError('Last N messages must be between 1 and 500, or leave empty for no limit.');
      return;
    }
    if (nDays != null && (Number.isNaN(nDays) || nDays < 1 || nDays > 365)) {
      setError('Last N days must be between 1 and 365, or leave empty for no limit.');
      return;
    }

    const maxSpeakers = speechMaxSpeakers.trim() ? parseInt(speechMaxSpeakers.trim(), 10) : null;
    if (maxSpeakers != null && (Number.isNaN(maxSpeakers) || maxSpeakers < 1 || maxSpeakers > 36)) {
      setError('Max speakers must be between 1 and 36, or leave empty for the default.');
      return;
    }
    const phraseWeight = speechWeight.trim() ? parseFloat(speechWeight.trim()) : null;
    if (phraseWeight != null && (Number.isNaN(phraseWeight) || phraseWeight < 0 || phraseWeight > 2)) {
      setError('Phrase list weight must be between 0.0 and 2.0, or leave empty for the default.');
      return;
    }
    if (speechPhrases.length > 500) {
      setError('Phrase list cannot contain more than 500 phrases.');
      return;
    }

    const speechConfigured =
      speechLanguage !== '' ||
      !speechDiarization ||
      !speechDiarizeIntermediate ||
      maxSpeakers != null ||
      speechPhrases.length > 0 ||
      phraseWeight != null;

    const speechConfigJson = speechConfigured
      ? JSON.stringify({
          language: speechLanguage || undefined,
          diarization: speechDiarization,
          diarizeIntermediateResults: speechDiarizeIntermediate,
          maxSpeakers: maxSpeakers ?? undefined,
          phraseList:
            speechPhrases.length > 0 || phraseWeight != null
              ? { phrases: speechPhrases, weight: phraseWeight ?? undefined }
              : undefined,
        } satisfies AgentSpeechConfig)
      : undefined;

    setSaving(true);
    try {
      const originalParamIds = new Set(_agent?.parameters.map(p => p.id) ?? []);

      const params = treeToParameters(inputFields).map((row) => ({
        ...(isEdit && row.id && originalParamIds.has(row.id) ? { id: row.id } : {}),
        key: row.key,
        label: row.label,
        description: row.description,
        type: row.type,
        defaultValue: row.defaultValue,
        isRequired: row.isRequired,
        isSecret: row.isSecret,
        validationJson: row.validationJson,
        sortOrder: row.sortOrder,
      }));

      if (isEdit && id) {
        const updateData: UpdateAgentRequest = {
          name: name.trim(),
          slug: slug.trim(),
          description: description.trim() || undefined,
          systemPrompt: systemPrompt.trim(),
          outputSchemaJson: outputSchemaJson.trim() || '{}',
          userPromptTemplate: userPromptTemplate.trim() || undefined,
          category: category || undefined,
          iconName: iconName.trim() || undefined,
          sortOrder,
          includeAllDefaultTools,
          fanOut,
          useOpenAiHostedSkills: selectedModelSupportsHostedSkills && useOpenAiHostedSkills,
          useHarnessAgent,
          injectSkillInstructionsAsUserContext,
          isActive,
          speechConfigJson,
          contextLimitLastNMessages: nMessages ?? null,
          contextLimitLastNDays: nDays ?? null,
          defaultModelId: defaultModelId || undefined,
          parameters: params as UpdateAgentParameterRequest[],
        };
        await agentService.updateAgent(id, updateData);
        addNotification('Agent updated successfully', 'success');
      } else {
        const createData: CreateAgentRequest = {
          name: name.trim(),
          slug: slug.trim() || undefined,
          description: description.trim() || undefined,
          systemPrompt: systemPrompt.trim(),
          outputSchemaJson: outputSchemaJson.trim() || '{}',
          userPromptTemplate: userPromptTemplate.trim() || undefined,
          category: category || undefined,
          iconName: iconName.trim() || undefined,
          sortOrder,
          includeAllDefaultTools,
          fanOut,
          useOpenAiHostedSkills: selectedModelSupportsHostedSkills && useOpenAiHostedSkills,
          useHarnessAgent,
          injectSkillInstructionsAsUserContext,
          speechConfigJson,
          contextLimitLastNMessages: nMessages ?? undefined,
          contextLimitLastNDays: nDays ?? undefined,
          defaultModelId: defaultModelId || undefined,
          parameters: params as CreateAgentParameterRequest[],
        };
        const created = await agentService.createAgent(createData);
        addNotification('Agent created successfully', 'success');
        navigate(`/agents/${created.id}`);
        return;
      }

      navigate(`/agents/${id}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save agent');
      addNotification(err.response?.data?.message || 'Failed to save agent', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleOutputModelChange = (fields: IoField[]) => {
    setOutputFields(fields);
    setOutputSchemaJson(treeToSchema(fields, outputRootExtra));
  };

  const handleApplyOutputJson = () => {
    const parsed = schemaToTree(outputSchemaJson);
    if (!parsed.supported) {
      setError('The JSON is not an object schema the tree editor can display. Fix the JSON or keep editing it directly.');
      return;
    }
    setError(null);
    setOutputFields(parsed.fields);
    setOutputRootExtra(parsed.rootExtra);
    setOutputSupported(true);
    setIoEditorKey((k) => k + 1);
  };

  const handleAddSpeechPhrase = () => {
    const phrase = speechPhraseInput.trim();
    if (!phrase) return;
    if (!speechPhrases.includes(phrase)) {
      setSpeechPhrases((prev) => [...prev, phrase]);
    }
    setSpeechPhraseInput('');
  };

  const handleRemoveSpeechPhrase = (phrase: string) => {
    setSpeechPhrases((prev) => prev.filter((p) => p !== phrase));
  };

  const declaredInputPaths = collectLeafPaths(inputFields);
  const referencedVariables = extractTemplateVariables(systemPrompt);
  const undeclaredVariables =
    declaredInputPaths.length > 0 ? referencedVariables.filter((v) => !declaredInputPaths.includes(v)) : [];
  const unreferencedFields = declaredInputPaths.filter((p) => !referencedVariables.includes(p));

  const handleInsertVariable = (path: string) => {
    const token = `{{${path}}}`;
    setSystemPrompt((prev) => (prev.includes(token) ? prev : `${prev}${prev.endsWith('\n') || prev === '' ? '' : '\n'}${token}`));
    navigator.clipboard?.writeText(token).catch(() => undefined);
  };

  return (
    <PageFormLayout
      title={isEdit ? 'Edit Agent' : 'Create Agent'}
      subtitle="Configure the agent profile, LLM model, prompt instructions, speech recognition and I/O schemas."
      data-testid="agent-form-page"
      loading={loading}
      error={error ?? undefined}
      onErrorDismiss={() => setError(null)}
      backTo={{ label: 'Back to List', href: '/agents' }}
      actions={{
        onSave: handleSubmit,
        onCancel: () => navigate('/agents'),
        saveLabel: isEdit ? 'Update' : 'Create',
        saving,
      }}
      navGroups={[
        {
          title: 'PROFILE',
          items: [
            { id: 'details', label: 'Details', icon: <Bot size={16} /> },
            { id: 'llm', label: 'LLM Configuration', icon: <Brain size={16} /> },
          ],
        },
        {
          title: 'BEHAVIOR',
          items: [
            { id: 'instructions', label: 'Instructions', icon: <FileText size={16} /> },
            { id: 'speech', label: 'Speech', icon: <Volume2 size={16} /> },
          ],
        },
        {
          title: 'DATA',
          items: [
            { id: 'io', label: 'Input / Output', icon: <ArrowLeftRight size={16} /> },
          ],
        },
      ]}
      sections={[
        {
          id: 'details',
          title: 'Details',
          description: 'Name, description, category, version, status and tags.',
          icon: <Bot size={18} />,
          content: (
            <FormSection
              title="Agent identity"
              description="Core fields that define how the agent appears and behaves in the platform."
            >
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField
                  label="Name"
                  required
                  description="Display name for this agent."
                  data-testid="agent-name"
                >
                  <Input
                    className="w-full"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="agent-name"
                  />
                </FormField>
                <FormField
                  label="Slug"
                  required
                  description="URL-friendly identifier (cannot be changed after creation)."
                  data-testid="agent-slug"
                >
                  <Input
                    className="w-full"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    required
                    disabled={isEdit}
                    data-testid="agent-slug"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField label="Description" description="Brief summary of what this agent does." data-testid="agent-description">
                  <Textarea
                    className="w-full"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    data-testid="agent-description"
                  />
                </FormField>
              </div>

              <div className="mt-5 grid gap-5 items-end sm:grid-cols-2">
                <FormField label="Category" description="Functional grouping for organisation." data-testid="agent-category">
                  <CoordioDropdown
                    options={categoryOptions}
                    value={category}
                    onChange={(val) => setCategory(val ?? '')}
                    clearable={false}
                    data-testid="agent-category"
                  />
                </FormField>
                <FormField label="Icon Name" description="Icon identifier (e.g. 'server', 'shield')." data-testid="agent-iconName">
                  <Input
                    className="w-full"
                    value={iconName}
                    onChange={(e) => setIconName(e.target.value)}
                    data-testid="agent-iconName"
                  />
                </FormField>
              </div>

              <div className="mt-5 grid gap-5 items-end sm:grid-cols-2">
                <FormField label="Sort Order" description="Controls display order in lists." data-testid="agent-sortOrder">
                  <Input
                    className="w-full"
                    type="number"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
                    data-testid="agent-sortOrder"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch checked={includeAllDefaultTools} onCheckedChange={(checked) => setIncludeAllDefaultTools(checked)} data-testid="agent-includeAllDefaultTools" />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Include All Default Tools</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch checked={fanOut} onCheckedChange={(checked) => setFanOut(checked)} data-testid="agent-fanOut" />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">FanOut PDF (run prompt per page)</span>
                    </div>
                  </div>
                </div>
              </div>

              {selectedModelSupportsHostedSkills && (
                <div className="mt-5">
                  <div className="ui-form-choice-field">
                    <div className="ui-form-choice-field__row">
                      <div className="ui-form-choice-field__control">
                        <Switch checked={useOpenAiHostedSkills} onCheckedChange={(checked) => setUseOpenAiHostedSkills(checked)} data-testid="agent-useOpenAiHostedSkills" />
                      </div>
                      <div className="ui-form-choice-field__label-cell">
                        <span className="ui-form-choice-field__label">OpenAI hosted skills</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-5">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch checked={useHarnessAgent} onCheckedChange={(checked) => setUseHarnessAgent(checked)} data-testid="agent-useHarnessAgent" />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Microsoft Agent Framework harness</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch checked={injectSkillInstructionsAsUserContext} onCheckedChange={(checked) => setInjectSkillInstructionsAsUserContext(checked)} data-testid="agent-injectSkillInstructionsAsUserContext" />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Inject skill instructions as user context</span>
                    </div>
                  </div>
                </div>
              </div>

              {isEdit && (
                <div className="mt-5">
                  <div className="ui-form-choice-field">
                    <div className="ui-form-choice-field__row">
                      <div className="ui-form-choice-field__control">
                        <Switch checked={isActive} onCheckedChange={(checked) => setIsActive(checked)} data-testid="agent-isActive" />
                      </div>
                      <div className="ui-form-choice-field__label-cell">
                        <span className="ui-form-choice-field__label">Active</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </FormSection>
          ),
        },
        {
          id: 'llm',
          title: 'LLM Configuration',
          description: 'Select the default model and configure context window limits.',
          icon: <Brain size={18} />,
          content: (
            <FormSection
              title="Default Model"
              description="The LLM model used when no per-session override is supplied."
            >
              <FormField label="Select Model" description="Choose the default LLM for this agent." data-testid="agent-defaultModelId">
                <CoordioDropdown
                  options={modelOptions}
                  value={defaultModelId}
                  onChange={(val) => setDefaultModelId(val ?? '')}
                  clearable={false}
                  disabled={modelsLoading}
                  data-testid="agent-defaultModelId"
                />
              </FormField>
              {availableModels.length === 0 && !modelsLoading && (
                <p className="text-xs text-[var(--color-text-secondary)] mt-2">
                  No models available. Configure LLM providers in Settings to enable model selection.
                </p>
              )}
              {modelsLoading && (
                <p className="text-xs text-[var(--color-text-secondary)] mt-2">
                  Loading available models…
                </p>
              )}

              <div className="mt-5 grid gap-5 items-end sm:grid-cols-2">
                <FormField label="Last N messages" description="1–500; only the most recent N messages. Leave empty for no limit." data-testid="agent-contextLimitLastNMessages">
                  <Input
                    className="w-full"
                    type="number"
                    value={contextLimitLastNMessages}
                    onChange={(e) => setContextLimitLastNMessages(e.target.value)}
                    placeholder="No limit"
                    min={1}
                    max={500}
                    data-testid="agent-contextLimitLastNMessages"
                  />
                </FormField>
                <FormField label="Last N days" description="1–365; only messages from the last N days. Leave empty for no limit." data-testid="agent-contextLimitLastNDays">
                  <Input
                    className="w-full"
                    type="number"
                    value={contextLimitLastNDays}
                    onChange={(e) => setContextLimitLastNDays(e.target.value)}
                    placeholder="No limit"
                    min={1}
                    max={365}
                    data-testid="agent-contextLimitLastNDays"
                  />
                </FormField>
              </div>
            </FormSection>
          ),
        },
        {
          id: 'instructions',
          title: 'Instructions',
          description: 'System prompt and template variable bindings.',
          icon: <FileText size={18} />,
          content: (
            <FormSection
              title="System Prompt"
              description="The base instructions sent to the model at the start of every conversation."
            >
              <FormField label="System Prompt" required description="Core instructions for the agent." data-testid="agent-systemPrompt">
                <CodeEditor
                  value={systemPrompt}
                  onChange={setSystemPrompt}
                  language="plaintext"
                  height="300px"
                  placeholder="Enter the system prompt for the agent..."
                  data-testid="agent-systemPrompt"
                />
              </FormField>
              {declaredInputPaths.length > 0 && (
                <div className="mt-4 rounded border border-blue-300 bg-blue-50 px-4 py-3 text-sm dark:border-blue-700 dark:bg-blue-950">
                  <p className="mb-2">
                    <strong>Data dictionary</strong> — click a field to insert its <strong>{'{{variable}}'}</strong> into the prompt (also copied to the clipboard).
                  </p>
                  <div className="flex gap-1.5 flex-wrap">
                    <TooltipProvider>
                      {declaredInputPaths.map((path) => (
                        <Tooltip key={path}>
                          <TooltipTrigger asChild>
                            <span>
                              <Chip
                                label={`{{${path}}}`}
                                variant={referencedVariables.includes(path) ? 'filled' : 'outlined'}
                                color={referencedVariables.includes(path) ? 'accent' : 'default'}
                                onClick={() => handleInsertVariable(path)}
                                className="cursor-pointer font-mono"
                              />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>Click to insert into the prompt</TooltipContent>
                        </Tooltip>
                      ))}
                    </TooltipProvider>
                  </div>
                  {unreferencedFields.length > 0 && (
                    <p className="text-xs text-[var(--color-text-secondary)] mt-2">
                      Not referenced in the prompt yet: {unreferencedFields.map((p) => `{{${p}}}`).join(', ')}
                    </p>
                  )}
                </div>
              )}
              {undeclaredVariables.length > 0 && (
                <div className="mt-4 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-700 dark:bg-amber-950">
                  The prompt references variables that are not declared in the Input Model:{' '}
                  {undeclaredVariables.map((v) => `{{${v}}}`).join(', ')}. They will render as empty text at run time.
                </div>
              )}
            </FormSection>
          ),
        },
        {
          id: 'speech',
          title: 'Speech',
          description: 'Speech-to-text settings for agent-scoped audio transcription.',
          icon: <Volume2 size={18} />,
          content: (
            <FormSection
              title="Transcription settings"
              description="Speech-to-text settings used when audio is transcribed through this agent. Azure Speech credentials stay on the server; only recognition options are stored here."
            >
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField label="Language" description="Recognition locale for speech-to-text." data-testid="agent-speechLanguage">
                  <CoordioDropdown
                    options={speechLanguageOptions}
                    value={speechLanguage}
                    onChange={(val) => setSpeechLanguage(val ?? '')}
                    clearable={false}
                    data-testid="agent-speechLanguage"
                  />
                </FormField>
                <FormField label="Max speakers" description="Diarization speaker cap (1–36)." data-testid="agent-speechMaxSpeakers">
                  <Input
                    className="w-full"
                    type="number"
                    value={speechMaxSpeakers}
                    onChange={(e) => setSpeechMaxSpeakers(e.target.value)}
                    placeholder="Default (10)"
                    min={1}
                    max={36}
                    data-testid="agent-speechMaxSpeakers"
                  />
                </FormField>
              </div>

              <div className="mt-5">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch checked={speechDiarization} onCheckedChange={(checked) => setSpeechDiarization(checked)} data-testid="agent-speechDiarization" />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Diarization</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5">
                <div className="ui-form-choice-field">
                  <div className="ui-form-choice-field__row">
                    <div className="ui-form-choice-field__control">
                      <Switch checked={speechDiarizeIntermediate} onCheckedChange={(checked) => setSpeechDiarizeIntermediate(checked)} data-testid="agent-speechDiarizeIntermediate" />
                    </div>
                    <div className="ui-form-choice-field__label-cell">
                      <span className="ui-form-choice-field__label">Diarize intermediate results</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5 grid gap-5 items-end sm:grid-cols-2">
                <FormField label="New phrase" description="Press Enter or click Add phrase." data-testid="agent-speechPhraseInput">
                  <Input
                    className="w-full"
                    value={speechPhraseInput}
                    onChange={(e) => setSpeechPhraseInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSpeechPhrase();
                      }
                    }}
                    data-testid="agent-speechPhraseInput"
                  />
                </FormField>
                <FormField label="Phrase list weight" description="0.0 disables, 2.0 maximum." data-testid="agent-speechWeight">
                  <Input
                    className="w-full"
                    type="number"
                    value={speechWeight}
                    onChange={(e) => setSpeechWeight(e.target.value)}
                    placeholder="Default (1.0)"
                    min={0}
                    max={2}
                    step={0.1}
                    data-testid="agent-speechWeight"
                  />
                </FormField>
              </div>
              <Button type="button" variant="outline" onClick={handleAddSpeechPhrase} className="mt-5">
                Add phrase
              </Button>
              {speechPhrases.length > 0 && (
                <div className="mt-4 flex gap-1.5 flex-wrap">
                  {speechPhrases.map((phrase) => (
                    <Chip
                      key={phrase}
                      label={phrase}
                      onRemove={() => handleRemoveSpeechPhrase(phrase)}
                    />
                  ))}
                </div>
              )}
            </FormSection>
          ),
        },
        {
          id: 'io',
          title: 'Input / Output',
          description: 'Define the input parameters and structured output schema for this agent.',
          icon: <ArrowLeftRight size={18} />,
          content: (
            <FormSection
              title="Input Model"
              description="Fields callers fill in when running this agent — an input form is generated automatically. Use the Object type to nest fields; only leaf fields become {{variables}}."
            >
              <IoModelEditor
                key={`input-${ioEditorKey}`}
                mode="input"
                initialFields={inputFields}
                onChange={setInputFields}
              />

              <div className="mt-5">
                <FormSection
                  title="Output Model"
                  description="Optional structured response contract. Each field's description is an instruction to the model about what to return. When configured, the agent returns JSON only."
                >
                  {outputSupported ? (
                    <>
                      <IoModelEditor
                        key={`output-${ioEditorKey}`}
                        mode="output"
                        initialFields={outputFields}
                        onChange={handleOutputModelChange}
                      />
                      <Button type="button" variant="ghost" size="sm" onClick={() => setShowOutputJson((v) => !v)} className="mt-2">
                        {showOutputJson ? 'Hide advanced (JSON)' : 'Advanced (JSON)'}
                      </Button>
                      <Collapse in={showOutputJson} unmountOnExit>
                        <div className="mt-2">
                          <CodeEditor
                            value={outputSchemaJson}
                            onChange={setOutputSchemaJson}
                            language="json"
                            height="220px"
                            placeholder='{"type":"object","properties":{}}'
                          />
                          <Button type="button" variant="outline" size="sm" onClick={handleApplyOutputJson} className="mt-2">
                            Apply JSON to tree
                          </Button>
                        </div>
                      </Collapse>
                    </>
                  ) : (
                    <>
                      <div className="mb-4 rounded border border-blue-300 bg-blue-50 px-4 py-3 text-sm dark:border-blue-700 dark:bg-blue-950">
                        This output schema uses JSON Schema constructs the tree editor cannot display
                        (for example a non-object root). Edit the JSON directly.
                      </div>
                      <CodeEditor
                        value={outputSchemaJson}
                        onChange={setOutputSchemaJson}
                        language="json"
                        height="220px"
                        placeholder='{"type":"object","properties":{}}'
                      />
                      <Button type="button" variant="outline" size="sm" onClick={handleApplyOutputJson} className="mt-2">
                        Try tree editor
                      </Button>
                    </>
                  )}
                </FormSection>
              </div>
            </FormSection>
          ),
        },
      ]}
    />
  );
};
